import type { SpanKindLabel, SpanRecord } from '@apm/contracts';
import { sqlVerb as coreSqlVerb, isSessionSql, isTxControlSql } from '@apm/core';
import type { ChipTone } from '../ui';

/**
 * 구간별 소요시간 막대의 배치 계산 (7회차 결정 3, prompts.md 7회차).
 *
 * 캔버스가 아니라 DOM으로 그린다. 한 요청의 span은 많아야 수백 개라 X-View(수만 점)와
 * 사정이 다르고, 막대마다 문장·오류 코드를 붙여야 해서 DOM 쪽이 훨씬 다루기 쉽다.
 *
 * 여기 있는 것은 전부 순수 함수다. 컴포넌트는 결과를 style에 얹기만 한다.
 */

export interface SpanBar {
  span: SpanRecord;
  /** 트랜잭션 전체 폭 대비 왼쪽 여백(%) */
  leftPct: number;
  /** 트랜잭션 전체 폭 대비 막대 길이(%). 0이어도 눈에 보이게 하한을 둔다 */
  widthPct: number;
  /** 트랜잭션 시작으로부터 몇 ms 뒤에 시작했는지 */
  offsetMs: number;
}

export interface Timeline {
  /** 트랜잭션 시작 시각. epoch ms */
  startedAt: number;
  /** 시간축 전체 길이 */
  totalMs: number;
  bars: SpanBar[];
}

/** 0폭 막대도 보이도록. 아주 짧은 SQL이 줄에서 사라지면 개수를 못 센다 */
const MIN_WIDTH_PCT = 0.4;

/**
 * span 목록 → 막대 배치. 시간축은 root가 아니라 **실제로 관측된 구간**을 쓴다.
 * root보다 늦게 끝난 자식(응답 후 정리 작업)이 있어도 잘리지 않는다.
 */
export function layoutSpans(spans: readonly SpanRecord[]): Timeline {
  if (spans.length === 0) return { startedAt: 0, totalMs: 0, bars: [] };

  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const span of spans) {
    if (span.startedAt < start) start = span.startedAt;
    const finish = span.startedAt + span.durationMs;
    if (finish > end) end = finish;
  }
  // 전부 같은 순간에 시작·종료한 경우(1ms 미만) 0으로 나누지 않게 한다
  const totalMs = Math.max(end - start, 1);

  const bars = [...spans]
    .sort((a, b) => a.startedAt - b.startedAt || b.durationMs - a.durationMs)
    .map((span) => {
      const offsetMs = span.startedAt - start;
      const widthPct = Math.max((span.durationMs / totalMs) * 100, MIN_WIDTH_PCT);
      return {
        span,
        offsetMs,
        leftPct: Math.min((offsetMs / totalMs) * 100, 100 - MIN_WIDTH_PCT),
        widthPct,
      };
    });

  return { startedAt: start, totalMs, bars };
}

/** 막대 색. X-View 점 색과 계열을 맞춘다. 오류는 무조건 빨강 */
export const SPAN_KIND_BAR: Record<SpanKindLabel, string> = {
  root: 'bg-accent',
  sql: 'bg-http',
  ext: 'bg-sse',
  internal: 'bg-muted',
};

export const SPAN_KIND_TONE: Record<SpanKindLabel, ChipTone> = {
  root: 'http',
  sql: 'mcp',
  ext: 'sse',
  internal: 'neutral',
};

/**
 * SQL 문장의 종류. 목록의 '종류' 열에 쓴다.
 * 판정은 `@apm/core`에 있다(22-B회차). 서버의 느린 SQL 통계가 같은 판정으로 트랜잭션 제어를 뺀다.
 * 문장이 없으면 '-'
 */
export function sqlVerb(query: string | undefined): string {
  return coreSqlVerb(query) ?? '-';
}

/**
 * 접어 둘 문장 나누기 (22-B회차 트랜잭션 제어, 23-A회차 세션 설정. 사용자 확정 4·22).
 *
 * `START TRANSACTION`·`COMMIT`도, `SELECT set_config('app.tenant_id', $1, true)`도 DB 왕복이라
 * 목록에 남기되 기본으로 접는다. 둘은 접는 이유가 달라(경계 / 설정) 따로 세고 따로 편다.
 * 원래 순서를 잃지 않게 각 span의 자리(1부터)를 함께 준다.
 * 판정은 `@apm/core`에 있고 서버의 느린 SQL 통계가 같은 판정으로 뺀다.
 */
export type SqlItems = { span: SpanRecord; index: number }[];

export interface SqlSplit {
  /** 접지 않는 문장. 자리는 전체 목록 기준이다 */
  queries: SqlItems;
  /** 트랜잭션 제어 문장 */
  control: SqlItems;
  /** 세션 설정 문장 */
  session: SqlItems;
}

export function splitSql(spans: readonly SpanRecord[]): SqlSplit {
  const queries: SqlItems = [];
  const control: SqlItems = [];
  const session: SqlItems = [];
  spans.forEach((span, at) => {
    const item = { span, index: at + 1 };
    // 트랜잭션 제어를 먼저 본다. 두 판정이 겹치는 문장은 없지만 순서를 못 박아 둔다
    if (isTxControlSql(span.query)) control.push(item);
    else if (isSessionSql(span.query)) session.push(item);
    else queries.push(item);
  });
  return { queries, control, session };
}

/**
 * 문장이 잘렸을 수 있는 길이 (22-B회차, UI 피드백 3).
 *
 * 에이전트의 속성 길이 상한은 32,768자다(22-C회차. 그 전은 4,096). 그 근처면 뒤가 잘린 것일 수
 * 있다. 22-C회차 에이전트는 잘린 문장에 `queryTruncated`를 붙여 주므로 확정이고, 옛 에이전트가
 * 보낸 행이나 22-C 전에 저장된 행(4,096자에서 잘렸지만 표시가 없다)은 길이로만 짐작한다.
 * 짐작 기준을 옛 상한 근처에 두면 표시 없는 옛 행을 잡을 수 있지만 지금 에이전트가 보낸 5,000자
 * 정상 문장까지 "잘렸을 수 있다"로 보이므로 새 상한 근처로 옮겼다.
 */
const TRUNCATION_GUESS_LENGTH = 32_000;

export function truncationHint(
  span: Pick<SpanRecord, 'query' | 'queryTruncated'>,
): string | undefined {
  if (span.queryTruncated === true) return '(뒤가 잘렸다 · 수집 상한)';
  if (span.query !== undefined && span.query.length >= TRUNCATION_GUESS_LENGTH) {
    return '(잘렸을 수 있다)';
  }
  return undefined;
}

export function spansOfKind(spans: readonly SpanRecord[], kind: SpanKindLabel): SpanRecord[] {
  return spans.filter((span) => span.kind === kind);
}

/** 요약의 `sqlErrorCount`와 별개로, 지금 화면에 있는 구간에서 센 DB 오류 수 */
export function sqlErrorCount(spans: readonly SpanRecord[]): number {
  return spans.filter((span) => span.kind === 'sql' && span.status === 'error').length;
}

/**
 * 같은 문장끼리 묶기 (후보 #9 — N+1 찾기).
 *
 * 한 요청 안에서 같은 SQL이 수십 번 돌면 목록을 아무리 훑어도 눈에 안 들어온다.
 * 문장은 감시 부품이 값을 `?`로 지운 뒤라 **문자열 동등 비교만으로** 접힌다. 정규화가 없다.
 * 묶음은 건수 내림차순, 같으면 소요 합계 내림차순이다. 실제로 문제인 것이 위로 온다.
 */
export interface SqlGroup {
  /** 묶음 대표 문장. 값이 없는 span은 이름으로 묶는다 */
  query: string;
  spans: SpanRecord[];
  count: number;
  totalMs: number;
  maxMs: number;
  errorCount: number;
  /** 이 요청 전체에서 이 문장이 처음 나온 자리(1부터) */
  firstIndex: number;
}

export function groupByQuery(spans: readonly SpanRecord[]): SqlGroup[] {
  const groups = new Map<string, SqlGroup>();
  spans.forEach((span, index) => {
    const key = span.query ?? span.name;
    const found = groups.get(key);
    const group = found ?? {
      query: key,
      spans: [],
      count: 0,
      totalMs: 0,
      maxMs: 0,
      errorCount: 0,
      firstIndex: index + 1,
    };
    group.spans.push(span);
    group.count += 1;
    group.totalMs += span.durationMs;
    group.maxMs = Math.max(group.maxMs, span.durationMs);
    if (span.status === 'error') group.errorCount += 1;
    if (!found) groups.set(key, group);
  });
  return [...groups.values()].sort(
    (a, b) => b.count - a.count || b.totalMs - a.totalMs || a.firstIndex - b.firstIndex,
  );
}

/**
 * 따옴표 없이 그대로 적을 값. 숫자와 `null`·`true`·`false`뿐이다.
 * 에이전트가 `String(value)`로 맞춰 보내므로 대소문자는 이 모양 하나다.
 */
const BARE_PARAM = /^(?:-?\d+(?:\.\d+)?|null|true|false)$/;

/**
 * `$1`·`$2`… 자리에 실제로 넘긴 값을 끼워 넣는다 (2026-09-07 결정 7, 19회차 화면 표시).
 *
 * 값을 그대로 못 보면 "이 조회가 왜 아무것도 못 찾았나"를 화면에서 알 수 없다.
 * 에이전트는 바인드 값을 전부 문자열로 보내므로(계약은 그대로 둔다) 여기서 읽기 좋게만 가른다 —
 * 숫자와 `null`·`true`·`false`는 그대로, 나머지는 작은따옴표로 감싼다. `LIMIT '1' OFFSET '0'`처럼
 * 보이면 원래 문장과 너무 달라 읽기 어렵다(32회차). 실행 가능한 SQL을 만드는 것이 아니라
 * **읽기 위한 것**이라 형 판정은 이 정도로 그친다.
 * 자리보다 값이 모자라면 그 자리는 그대로 둔다.
 */
export function applyParams(query: string, params?: readonly string[]): string {
  if (params === undefined || params.length === 0) return query;
  return query.replace(/\$(\d+)/g, (whole, digits: string) => {
    const value = params[Number(digits) - 1];
    if (value === undefined) return whole;
    if (BARE_PARAM.test(value)) return value;
    return `'${value.replace(/'/g, "''")}'`;
  });
}
